import {
  useEffect,
  useId,
  useRef,
  useSyncExternalStore,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react"

import { ownCloseHandler } from "./dialog-events"
import { uploadAccept } from "./upload"

/* The few marks the portal draws more than once. Everything else a client
   sees is type on paper. */

export function InlineError({ message }: { message: string }) {
  return (
    <p className="inline-error" role="alert">
      {message}
    </p>
  )
}

/** The one live line that says a file was sent or was not. It stays
 * mounted, so a screen reader hears each change. */
export function UploadAnnouncer({ text }: { text: string }) {
  return (
    <p className="visually-hidden" role="status">
      {text}
    </p>
  )
}

/** A section that could not load, with the one way back. */
export function RetryNotice({
  message,
  onRetry,
}: {
  message: string
  onRetry(): void
}) {
  return (
    <div className="inline-error action-error" role="alert">
      <span>{message}</span>
      <button className="ghost-pill small" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

const coarsePointerQuery = "(pointer: coarse)"

/* Kept at module level, so React subscribes once and not on every render. */
const subscribeCoarsePointer = (onChange: () => void) => {
  const query = window.matchMedia(coarsePointerQuery)
  query.addEventListener("change", onChange)
  return () => query.removeEventListener("change", onChange)
}
const coarsePointerNow = () => window.matchMedia(coarsePointerQuery).matches
const noCoarsePointer = () => false

/** True on a touch screen, where a file field can open the camera. Read
 * again when the pointer changes, as on a tablet that docks a keyboard.
 * False where there is no window, as in a render on the server. */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    subscribeCoarsePointer,
    coarsePointerNow,
    noCoarsePointer
  )
}

/* The width at which the portal header drops its sections for the tab bar,
   the same 720px styles.css uses. */
const narrowScreenQuery = "(max-width: 720px)"
const subscribeNarrowScreen = (onChange: () => void) => {
  const query = window.matchMedia(narrowScreenQuery)
  query.addEventListener("change", onChange)
  return () => query.removeEventListener("change", onChange)
}
const narrowScreenNow = () => window.matchMedia(narrowScreenQuery).matches
const notNarrowScreen = () => false

/** True on a phone-width screen, where the header has room for the firm's
 * name and the way out only. */
export function useNarrowScreen(): boolean {
  return useSyncExternalStore(
    subscribeNarrowScreen,
    narrowScreenNow,
    notNarrowScreen
  )
}

/** The file field every "Add a file" button opens. The reset after a choice
 * lets a client pick the same file twice. `camera` opens the phone's rear
 * camera instead, for "Take a photo"; the photo goes the same way a file
 * does. */
export function HiddenFileInput({
  ref,
  multiple,
  camera = false,
  onFiles,
}: {
  ref: RefObject<HTMLInputElement | null>
  multiple?: boolean
  camera?: boolean
  onFiles(files: FileList): void
}) {
  return (
    <input
      ref={ref}
      className="visually-hidden-input"
      type="file"
      accept={camera ? "image/*" : uploadAccept}
      capture={camera ? "environment" : undefined}
      multiple={camera ? false : multiple}
      onChange={(event) => {
        if (event.currentTarget.files?.length) {
          onFiles(event.currentTarget.files)
          event.currentTarget.value = ""
        }
      }}
    />
  )
}

export function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  )
}

export function FileGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Zm0 0v6h6" />
    </svg>
  )
}

export function LedgerBoxMark() {
  return (
    <svg className="ledgerbox-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.7 20 7v10l-8 4.3L4 17V7l8-4.3Zm0 2.7L7 8.1v7.8l5 2.7 5-2.7V8.1L12 5.4Zm-3.7 4L12 11.5l3.7-2.1v2.7L12 14.2l-3.7-2.1V9.4Z" />
    </svg>
  )
}

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The portal's one modal: a native dialog, so the page behind it is inert
 * and Escape closes it. The Tab wrap, the scroll lock and the focus return
 * are written out, because the top layer lets Tab leave for the browser's
 * own controls and not every browser puts focus back where it was. A press
 * on the scrim lands on the dialog itself, never on its panel, so only that
 * press closes it. A dialog nested inside it closes on its own.
 */
export function ModalDialog({
  id,
  open,
  onClose,
  labelledBy,
  className,
  initialFocus,
  children,
}: {
  id?: string
  open: boolean
  onClose(): void
  labelledBy: string
  className: string
  /** Takes focus on open; the first control does otherwise. */
  initialFocus?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const node = dialog.current
    if (!node) return
    if (open && !node.open) {
      returnTo.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      node.showModal()
      initialFocus?.current?.focus()
    }
    if (!open && node.open) node.close()
  }, [open])

  // The page behind stays where it was while the dialog is open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  const wrapTab = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== "Tab") return
    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(focusableSelector)
    )
    const first = focusable.at(0)
    const last = focusable.at(-1)
    if (!first || !last) return
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <dialog
      ref={dialog}
      id={id}
      className={className}
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClose={ownCloseHandler(() => {
        onClose()
        returnTo.current?.focus()
      })}
      onKeyDown={wrapTab}
      onClick={(event) => {
        if (event.target === event.currentTarget) event.currentTarget.close()
      }}
    >
      {children}
    </dialog>
  )
}

/**
 * A yes-or-no question before something that cannot be taken back, on the
 * portal's one modal. Focus starts on Cancel, so Enter never removes by
 * accident.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  onConfirm(): void
  onCancel(): void
}) {
  const titleId = useId()
  const cancel = useRef<HTMLButtonElement>(null)
  return (
    <ModalDialog
      open={open}
      onClose={onCancel}
      labelledBy={titleId}
      className="confirm-dialog"
      initialFocus={cancel}
    >
      <h2 id={titleId}>{title}</h2>
      <p>{body}</p>
      <div className="confirm-dialog-actions">
        <button
          ref={cancel}
          type="button"
          className="ghost-pill"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="button" className="ghost-pill is-danger" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </ModalDialog>
  )
}
